import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit, X, Save } from "lucide-react";
import { toast } from "react-toastify";
import {
  listCustomFields,
  createCustomField,
  updateCustomField,
  archiveCustomField,
} from "../../apis/contactCustomFields";

const TYPES = ["text", "number", "date", "boolean", "select", "url", "email", "phone"];

const CustomFieldsPage = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchFields = async () => {
    setLoading(true);
    try {
      const r = await listCustomFields({ include_archived: true });
      setFields(r.data.fields || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load custom fields");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFields(); }, []);

  const onArchive = async (id) => {
    if (!window.confirm("Archive this custom field? Existing values are preserved.")) return;
    try {
      await archiveCustomField(id);
      toast.success("Field archived");
      fetchFields();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to archive");
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Custom Fields</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Account-wide registry. Values are stored per contact under <code>custom_fields</code>.
            </p>
          </div>
          <button
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            <Plus size={14} /> New field
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left">Label</th>
                <th className="px-3 py-2.5 text-left">Key</th>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-left">Required</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 w-28" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : fields.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-12 text-center text-gray-400">No custom fields yet.</td></tr>
              ) : fields.map((f) => (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2.5 font-medium text-gray-900">{f.label}</td>
                  <td className="px-3 py-2.5 text-gray-600">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{f.field_key}</code>
                  </td>
                  <td className="px-3 py-2.5 capitalize">{f.field_type}</td>
                  <td className="px-3 py-2.5">{f.is_required ? "Yes" : "No"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${f.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>{f.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={() => setEditing(f)} className="p-1.5 rounded text-gray-600 hover:bg-gray-100" title="Edit">
                      <Edit size={14} />
                    </button>
                    {f.status === "active" && (
                      <button onClick={() => onArchive(f.id)} className="p-1.5 rounded text-rose-600 hover:bg-rose-50" title="Archive">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <CustomFieldFormModal
          field={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchFields();
          }}
        />
      )}
    </div>
  );
};

const CustomFieldFormModal = ({ field, onClose, onSaved }) => {
  const [form, setForm] = useState({
    label: field?.label || "",
    field_key: field?.field_key || "",
    field_type: field?.field_type || "text",
    options: Array.isArray(field?.options) ? field.options.join(", ") : (field?.options || ""),
    is_required: field?.is_required ? true : false,
    display_order: field?.display_order || 0,
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!field?.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.label) {
      toast.error("Label is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label,
        field_type: form.field_type,
        is_required: !!form.is_required,
        display_order: parseInt(form.display_order, 10) || 0,
        options: form.field_type === "select" && form.options
          ? form.options.split(",").map((s) => s.trim()).filter(Boolean)
          : null,
      };
      if (!isEdit && form.field_key) payload.field_key = form.field_key;
      if (isEdit) {
        await updateCustomField(field.id, payload);
        toast.success("Field updated");
      } else {
        await createCustomField(payload);
        toast.success("Field created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">
            {isEdit ? "Edit field" : "New custom field"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <Field label="Label">
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="cf-input" />
          </Field>
          <Field label="Key (snake_case)" hint={isEdit ? "Cannot be changed after creation" : "Auto-derived from label if blank"}>
            <input
              value={form.field_key}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, field_key: e.target.value })}
              className="cf-input"
            />
          </Field>
          <Field label="Type">
            <select value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })} className="cf-input">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          {form.field_type === "select" && (
            <Field label="Options (comma-separated)">
              <input value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} className="cf-input" />
            </Field>
          )}
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.is_required} onChange={(e) => setForm({ ...form, is_required: e.target.checked })} />
            <span className="text-sm text-gray-700">Required field</span>
          </label>
          <Field label="Display order">
            <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} className="cf-input" />
          </Field>
          <style>{`.cf-input { display:block; width:100%; font-size:0.875rem; border:1px solid #e5e7eb; border-radius:0.375rem; padding:0.5rem 0.75rem; }`}</style>
        </form>
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button onClick={onClose} type="button" className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <span className="text-xs font-medium text-gray-600">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

export default CustomFieldsPage;
